import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../shared/ui';
import ModalConfirm from '../../shared/ui/modal-confirm/ui/ModalConfirm';
import Filter from '../../shared/ui/filter/Filter';
import CatalogPagination from '../../features/pagination';
import CustomSelect from '../../shared/ui/filter/LimitSelect';
import { getAdminPlacementTestSessions, deleteAdminPlacementTestSession } from '../../shared/api/placement-tests.services';
import { ADULT_PLACEMENT_TEST_SLUG } from '../../shared/config/testSlugs';
import toast from '../../shared/lib/toast';
import { buildListQuery, buildPaginationMeta } from '../../shared/lib/buildListQuery';
import { translateStatus } from '../../shared/lib/statusTranslate';
import StatusBadge from '../../shared/ui/status-badge/StatusBadge';

export default function PlacementTestSessionsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const filters = [
    {
      key: 'status',
      type: 'checkbox',
      label: 'Статус',
      options: [
        { value: 'in_progress', label: translateStatus('in_progress') },
        { value: 'awaiting_contact', label: translateStatus('awaiting_contact') },
        { value: 'completed', label: translateStatus('completed') }
      ]
    }
  ];

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(searchParams.entries());
      // Сесій сотні, тому пошук за контактом і фільтр статусу виконує сервер,
      // а сторінка передає йому offset — раніше йшов page, який сервер ігнорував.
      const { query, page, limit } = buildListQuery(params, { extraKeys: ['status'] });

      const data = await getAdminPlacementTestSessions({
        ...query,
        templateSlug: ADULT_PLACEMENT_TEST_SLUG,
      });

      setSessions({
        ...data,
        meta: buildPaginationMeta({ page, limit, total: data?.total }),
      });
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Помилка при завантаженні сесій');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [searchParams]);

  const handleDelete = async (sessionId) => {
    if (window.confirm('Ви впевнені, що хочете видалити цю сесію?')) {
      try {
        const response = await deleteAdminPlacementTestSession(sessionId);
        if (response?.ok || !response?.error) {
          toast.success('Сесія успішно видалена!');
          fetchSessions();
        } else {
          toast.error(response?.message || 'Помилка при видаленні');
        }
      } catch (err) {
        toast.error('Помилка при видаленні');
      }
    }
  };

  return (
    <>
      <ModalConfirm title="Ви впевнені, що хочете видалити цю сесію?" />

      <PageHeader
        placeholder={'Пошук за імʼям, телефоном або номером сесії'}
        filters={filters}
      />
      <Filter fields={filters} />

      <section className="section-admin">
        {loading ? (
          <p>Завантаження...</p>
        ) : (
          <div className='admin-list'>
            <div className="admin-list__header">
              <p className="admin-list__header-info">
                {`${sessions?.total || 0} сесій`}
              </p>
              <div className="admin-list__header-func">
                <CustomSelect options={[10, 20, 50]} />
              </div>
            </div>

            <div className="admin-list__content">
              <ul className="admin-list__content-titles" style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1fr 1fr', gap: '24px', padding: '16px 12px', borderBottom: '1px solid #D9D9D9' }}>
                <li>Імʼя / Телефон</li>
                <li>Рівень / Результат</li>
                <li>Дата</li>
                <li>Статус</li>
                <li>Дія</li>
              </ul>
              <ul className="admin-list__content-items">
                {sessions.items?.length > 0 ? (
                  sessions.items.map((session) => (
                    <div key={session.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1fr 1fr', gap: '24px', alignItems: 'center', padding: '16px 12px', marginBottom: '16px', borderBottom: '1px solid #D9D9D9' }}>
                      <div>
                        <p style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 8px 0', color: '#0F172A' }}>
                          {session.contact?.name || 'N/A'}
                        </p>
                        <p style={{ fontSize: '13px', color: '#94A3B8', margin: 0 }}>
                          {session.contact?.phone || 'N/A'}
                          {session.contact?.age != null ? ` · ${session.contact.age} р.` : ''}
                        </p>
                      </div>

                      <div>
                        <p style={{ fontSize: '15px', fontWeight: '600', margin: '0 0 8px 0', color: '#0F172A' }}>
                          {session.result?.level || 'N/A'}
                        </p>
                        <p style={{ fontSize: '13px', color: '#94A3B8', margin: 0 }}>
                          {session.result?.percentage || 0}%
                        </p>
                      </div>

                      <div>
                        <p style={{ fontSize: '14px', margin: 0, color: '#0F172A' }}>
                          {new Date(session.createdAt).toLocaleDateString('uk-UA')}
                        </p>
                        <p style={{ fontSize: '13px', color: '#94A3B8', margin: '8px 0 0 0' }}>
                          {new Date(session.createdAt).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>

                      <div>
                        <StatusBadge status={session.status} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <button
                          onClick={() => navigate(`/placement-tests-sessions/${session.id}`)}
                          style={{
                            backgroundColor: '#2D9E6B',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Переглянути
                        </button>
                        <button
                          onClick={() => handleDelete(session.id)}
                          style={{
                            backgroundColor: '#FE0707',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Видалити
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <li className="admin-list__empty">Нічого не знайдено</li>
                )}
              </ul>
            </div>

            <div className="admin-list__pagination">
              <CatalogPagination data={sessions?.meta} />
            </div>
          </div>
        )}
      </section>
    </>
  );
}
